import React from 'react';

import css from './TermsOfServicePage.module.css';

const TermsOfService = () => {
  return (
    <div className={css.content}>
      <h1>Terms of Service</h1>
      <p className={css.updatedOn}>
        <strong>Last updated:</strong> January 29, 2026
      </p>

      <section className={css.section}>
        <h2>1. Marketplace Platform</h2>
        <p>
          This site is a marketplace platform that connects users. Stay Greedless is not a party to
          any rental agreement or transaction between users of this site. All users are responsible
          for their own actions on this platform and for any transaction agreement entered into as a
          result of a property listing on this platform.
        </p>
        <p>
          Stay Greedless is not acting as an agent for any user and does not own any property listed
          on the platform. All hosts are required to comply with the laws that apply to hosting
          property in their jurisdiction. We provide a platform texting tool to enable users to
          communicate with each other.
        </p>
        <p>
          Stay Greedless may be legally obligated to cooperate with governmental bodies in
          connection with investigations involving users on this platform. Stay Greedless will take
          precautions to prevent illegal activity against our users.
        </p>
      </section>

      <section className={css.section}>
        <h2>2. User Conduct</h2>
        <p>
          All users must always be respectful to other users and to Stay Greedless staff or lose the
          privilege of using this platform.
        </p>
      </section>

      <section className={css.section}>
        <h2>3. Your Email</h2>
        <p>
          When you provide your email address, you agree that Stay Greedless and its affiliates may
          add your email to their user database, and you may receive some promotional emails each
          month.
        </p>
      </section>

      <section className={css.section}>
        <h2>4. Payment Processing</h2>
        <p>
          Stay Greedless uses PayPal and Stripe to process marketplace payments. The processor
          available for a transaction may depend on the host&apos;s connected account, the
          guest&apos;s selected payment method, currency, country, and processor availability. Use
          of either processor is subject to that processor&apos;s applicable user agreements,
          acceptable use requirements, privacy practices, merchant or connected-account terms, and
          other rules.
        </p>
        <p>
          Users authorize Stay Greedless to transmit transaction instructions and information to
          PayPal or Stripe as necessary to initiate and administer payments, platform fees,
          settlement, payouts, voids, refunds, disputes, and related payment activity.
        </p>
        <p>
          Guests must use a valid payment method that they are authorized to use and must not
          initiate an improper chargeback or payment dispute. All booking payments must remain
          within the Stay Greedless checkout flow using the selected processor. Payments requested
          or made outside the platform are not protected or administered by Stay Greedless and may
          result in account or listing restrictions.
        </p>
        <p>
          PayPal or Stripe may approve, decline, limit, hold, reserve, delay, reverse, or otherwise
          restrict a transaction or account under its rules and applicable law. Settlement
          availability may be affected by processor review, account limitations, reserves, disputes,
          chargebacks, currency conversion, banking networks, weekends, or holidays. Stay Greedless
          does not control a processor&apos;s independent risk, compliance, or settlement decisions.
        </p>
      </section>

      <section className={css.section}>
        <h2>5. Marketplace Flow of Funds, Host Settlement, and Refunds</h2>
        <p>
          The host is the seller and service provider for each reservation. Stay Greedless operates
          as a marketplace. Booking funds are processed and held within PayPal&apos;s or
          Stripe&apos;s regulated payment infrastructure and are not held in a Stay Greedless bank
          account.
        </p>

        <h3>5.1 PayPal Flow</h3>
        <p>
          PayPal processes payments through its marketplace payment infrastructure. At checkout,
          PayPal authorizes the guest&apos;s payment method, but the payment is not captured at that
          time. Two days after the reservation ends, PayPal captures the authorized payment on
          behalf of the host. The host&apos;s connected PayPal merchant account is identified as the
          payee. PayPal settles the booking proceeds to the host, less applicable fees, and
          separately allocates Stay Greedless&apos;s disclosed platform fee.
        </p>
        <p>
          If a reservation is canceled before capture, the PayPal authorization is voided. If a
          refund is required after capture, it is processed through PayPal against the original
          transaction. The timing for funds or refunds to become available in a PayPal account or
          linked bank account is determined by PayPal, the account&apos;s status, and the applicable
          banking institution.
        </p>

        <h3>5.2 Stripe Flow</h3>
        <p>
          Stripe processes card payments through Stripe Connect. Each participating host must
          complete Stripe&apos;s connected-account onboarding and link an eligible payout account.
          The guest&apos;s card payment is authorized and captured through Stripe as part of booking
          confirmation. Stripe holds the transaction funds within its payment system until the
          transaction reaches the scheduled completion point. Stripe then transfers the host&apos;s
          net proceeds to the host&apos;s connected account and initiates payout to the host&apos;s
          linked bank account, while separately allocating applicable processing and disclosed
          platform fees.
        </p>
        <p>
          Stripe handles card refunds against the original transaction. Payout and refund timing is
          determined by Stripe, the connected account&apos;s country and status, the applicable
          payout schedule, and the receiving banking institution.
        </p>

        <h3>5.3 Seller Processor Requirements</h3>
        <p>
          Before accepting reservations, each host must connect an eligible PayPal merchant account
          or Stripe connected account through the applicable Stay Greedless seller-onboarding flow
          and complete all identity, business, beneficial ownership, tax, sanctions, bank-account,
          and other information requested by the processor. The host must keep its information
          accurate and current, maintain an account eligible to receive payments and payouts, grant
          the permissions required for marketplace payment processing, and keep the account in good
          standing.
        </p>
        <p>
          A host may connect only a PayPal or Stripe account that the host owns or is legally
          authorized to operate. The host must comply with the processor&apos;s merchant,
          connected-account, and acceptable-use requirements, respond promptly to verification or
          remediation requests, and notify Stay Greedless if the account becomes restricted,
          suspended, or unable to receive payments. Stay Greedless may pause listings,
          reservations, capture, transfer, payout, or settlement until applicable processor
          requirements are satisfied.
        </p>

        <h3>5.4 Seller Payment Responsibilities</h3>
        <p>
          As the seller and Merchant of Record, the host is responsible for accurately describing,
          pricing, and providing the accommodation; honoring confirmed reservations; complying with
          applicable licensing, tax, consumer-protection, and accommodation laws; providing
          appropriate transaction records or receipts; communicating with guests; and cooperating
          with cancellations, refunds, payment disputes, chargebacks, and processor investigations.
          Hosts are responsible for amounts PayPal, Stripe, or Stay Greedless properly assesses
          against them under the booking terms, including refunds, reversals, chargebacks,
          penalties, and disclosed fees.
        </p>
        <p>
          Stay Greedless may provide PayPal or Stripe with transaction records and other information
          needed to investigate or resolve payment activity. To the extent permitted by applicable
          law and the processor&apos;s rules, amounts a host owes may be collected through the
          processor, charged to an authorized payment method, or deducted from current or future
          settlement or payout.
        </p>
      </section>

      <section className={css.section}>
        <h2>6. Photos/Images</h2>
        <p>
          By posting images on this platform, you grant Stay Greedless the right to use your
          property images for advertising, marketing, commercials, and other purposes in any media
          or on any platform.
        </p>
        <ul>
          <li>Property images must be of the property you are listing on the platform.</li>
          <li>Property images must be clear of people and any controversial content.</li>
          <li>Stay Greedless may remove any images it considers inappropriate.</li>
          <li>
            All pictures submitted to this platform give Stay Greedless the right to use the
            likeness for marketing purposes on other platforms.
          </li>
          <li>All images go through a verification process that could take up to 48 hours.</li>
          <li>Users agree that they have the rights to all images they post on this platform.</li>
        </ul>
      </section>

      <section className={css.section}>
        <h2>7. Profile Picture</h2>
        <p>
          All users are required to have a current and clear headshot profile picture. A user&apos;s
          selfie picture will become the default profile picture, and the default picture can be
          changed by the user.
        </p>
      </section>

      <section className={css.section}>
        <h2>8. Host Insurance and Platform Protection</h2>
        <p>
          Stay Greedless is not an insurance company, insurance agent, broker, or underwriter and
          does not currently include or sell a third-party insurance policy with every reservation.
          In particular, Stay Greedless does not provide or promise $100,000 of liability insurance
          unless a separate policy is expressly offered and its insurer, underwriter, coverage
          limits, exclusions, and policy terms are disclosed to the user.
        </p>
        <p>
          Any property-damage assistance expressly offered by Stay Greedless is a contractual
          platform protection benefit, not insurance. Its availability, maximum benefit, eligible
          losses, exclusions, documentation requirements, and other conditions will be disclosed in
          the applicable booking or protection terms. Any stated maximum is an aggregate limit, not
          a guaranteed payment, and assistance may be reduced or denied based on the applicable
          terms and the results of the claim review.
        </p>
        <p>
          A host requesting property-damage assistance must promptly report the incident and provide
          the requested evidence, which may include photographs, receipts, repair estimates, guest
          communications, proof of ownership, and a police report where appropriate. Stay Greedless
          may investigate the claim and require the host to first pursue recovery from the
          responsible guest or other available sources. Review and payment timing depends on the
          completeness and complexity of the claim and is not guaranteed within a fixed period.
        </p>
        <p>
          Hosts remain responsible for obtaining and maintaining appropriate short-term rental,
          property, casualty, and liability insurance. Platform protection is not a substitute for
          insurance and does not eliminate a host&apos;s responsibility for deductibles, exclusions,
          uncovered losses, or compliance with applicable law. If Stay Greedless later offers a
          third-party insurance product, the insurer or underwriter and the controlling policy terms
          will be identified before enrollment or purchase.
        </p>
      </section>

      <section className={css.section}>
        <h2>9. Fees</h2>
        <h3>Guests</h3>
        <ul>
          <li>Guests are charged a fixed $18 service fee per booking.</li>
        </ul>
        <h3>Hosts</h3>
        <ul>
          <li>Hosts are charged a 3% transaction fee.</li>
        </ul>
      </section>

      <section className={css.section}>
        <h2>10. Cancellation Policy</h2>
        <p>Stay Greedless offers a standard cancellation policy for all users.</p>
        <ul>
          <li>Guests will receive a 100% refund if the host cancels at any point.</li>
          <li>
            Hosts will be charged a 3% cancellation fee if the booking is canceled within two weeks
            of the stay.
          </li>
          <li>
            All bookings made by guests are non-refundable unless the guest selected Make my trip
            refundable at checkout.
          </li>
          <li>
            Guests are charged an additional 3% fee at checkout if Make my trip refundable is
            selected.
          </li>
        </ul>

        <h3>10.2 Host/Property Inaccuracies</h3>
        <p>Guests may ask for a refund if:</p>
        <ul>
          <li>
            The host becomes unresponsive or is a no-show at check-in if the property is not
            self-check-in.
          </li>
          <li>The property does not match the listing description on the website.</li>
          <li>The property contains safety or health hazards.</li>
          <li>The property contains pests.</li>
          <li>The special amenities are non-functional.</li>
          <li>The property is not reasonably clean.</li>
        </ul>

        <h3>10.3 If a Host Cancels a Confirmed Reservation</h3>
        <ul>
          <li>
            If a reservation is canceled less than 14 days before check-in, the host will be charged
            5% of the reservation amount for canceling.
          </li>
          <li>
            If the host becomes known for canceling confirmed reservations, the host will be banned
            from hosting the property or properties on the platform.
          </li>
          <li>
            If a host cancels a confirmed reservation during a reservation for anything other than
            guests breaking house rules, the host or property will be banned from the platform.
          </li>
          <li>
            The host payment method on file will be charged for all violations. If Stay Greedless is
            unable to retrieve the funds, the host and all properties could be banned indefinitely.
          </li>
          <li>In some cases, host cancellation fees will be held from the next reservation.</li>
          <li>The minimum cancellation fee is $40.</li>
          <li>
            After a cancellation by the host, the booking calendar may be blocked on the canceled
            days for that property.
          </li>
        </ul>
      </section>

      <section className={css.section}>
        <h2>11. Check-in/Check-out</h2>
        <p>
          All hosts will determine their own check-in and check-out times for their property. If a
          guest wants to check in earlier or check out later, the guest must get approval from the
          host. After getting approval from the host, the guest may be charged an extra fee by the
          host.
        </p>
      </section>

      <section className={css.section}>
        <h2>12. Cleaning Fees</h2>
        <p>No cleaning fees are charged for reservations.</p>
      </section>

      <section className={css.section}>
        <h2>13. Hosting Posting Fees</h2>
        <p>All hosts may post their properties for free.</p>
      </section>

      <section className={css.section}>
        <h2>14. Subscription Fee</h2>
        <p>Stay Greedless does not charge a subscription fee.</p>
      </section>

      <section className={css.section}>
        <h2>15. Commission Fee</h2>
        <p>Stay Greedless does not charge a commission fee.</p>
      </section>

      <section className={css.section}>
        <h2>16. Pet Fee</h2>
        <p>Stay Greedless does not charge pet fees.</p>
      </section>

      <section className={css.section}>
        <h2>17. Identity Verification</h2>
        <p>
          Stay Greedless operates its own identity verification process. Users complete the secure
          verification form within the platform and submit the requested personal information, an
          unexpired government-issued identity document, and a current selfie or live image. Users
          must not send identity documents or selfies to Stay Greedless by email.
        </p>
        <p>
          Authorized Stay Greedless administrators review the submitted information through the
          administrative backend. The review may include checking that the document appears valid
          and unexpired, the document information matches the user&apos;s account information, and
          the identity document and submitted image appear to belong to the same person. Stay
          Greedless may request additional information or a new submission if the information is
          incomplete, unclear, expired, inconsistent, or indicates possible fraud.
        </p>
        <p>
          Hosts who connect a PayPal merchant account or Stripe connected account must also complete
          the applicable processor&apos;s separate merchant onboarding, identity, business,
          sanctions, and other compliance checks. Those checks are performed by PayPal or Stripe
          under the applicable processor&apos;s terms and are separate from Stay Greedless&apos;s
          internal identity review.
        </p>
        <p>
          A host may not create or publish a listing until an authorized administrator approves the
          identity review and enables listing access for the account. Processing times vary and are
          not guaranteed. Stay Greedless may deny, restrict, or suspend an account when verification
          cannot be completed or when submitted information is false, altered, expired, or does not
          match the account holder.
        </p>
      </section>

      <section className={css.section}>
        <h2>18. Promotions and Rewards</h2>
        <p>
          Stay Greedless does not currently operate the previously described booking-linked cash
          rewards program. Completing a booking does not enter a user into a drawing or create any
          right to receive a prize or cash award.
        </p>
        <p>
          Any future sweepstakes, contest, rewards program, or other promotion will be governed by
          separate Official Rules published before the promotion begins. Those Official Rules will
          identify the sponsor, promotion period, geographic and age eligibility, entry method,
          alternative free method of entry where required, prize details and approximate retail
          value, selection and notification process, odds or judging criteria, applicable
          state-specific restrictions, tax responsibilities, and other material conditions. No
          purchase or booking will be required where prohibited by law. All promotions are void
          where prohibited or restricted by law.
        </p>
      </section>

      <section className={css.section}>
        <h2>19. Scholarship Programs</h2>
        <p>
          Stay Greedless does not currently operate the previously described booking-linked college
          scholarship program and is not accepting applications or awarding scholarships under
          that program.
        </p>
        <p>
          Any future scholarship will be announced under separate published rules stating the
          sponsor, application period, eligibility criteria, geographic and age restrictions,
          selection criteria, award amount and permitted use, required documentation, recipient
          notification, tax treatment, privacy terms, and applicable state-specific restrictions.
          A future scholarship will not require a booking or purchase unless expressly permitted by
          applicable law and disclosed in its rules. All scholarship offers are void where
          prohibited or restricted by law.
        </p>
      </section>

      <section className={css.section}>
        <h2>20. Guest Responsibilities</h2>
        <p>
          Most of the time, you stay at your host&apos;s primary home. All guests must therefore
          treat the host&apos;s household with respect, the same way you would like guests to treat
          your home, caring for your host&apos;s home as if it were your own.
        </p>
        <p>
          By opening their home, the host is placing a lot of faith in you. As a guest, it is
          important to do your utmost to thank your host for their generosity by taking care of
          their home. You should leave the house in the same condition you found it when you
          arrived.
        </p>
        <h3>Cleaning Tips</h3>
        <ul>
          <li>Clean each room before your departure.</li>
          <li>Empty the refrigerator if necessary.</li>
          <li>Make the bed with clean sheets if available.</li>
          <li>Empty all trash cans.</li>
          <li>Put used sheets and towels away.</li>
        </ul>
        <p>
          Before check-in, you and your host should agree on the household responsibilities for your
          stay. Normally, the guest is responsible for leaving the house as it was found. It is the
          guest&apos;s responsibility to perform the different household chores agreed upon with the
          host.
        </p>
      </section>

      <section className={css.section}>
        <h2>21. Host Responsibilities</h2>
        <p>
          When a guest arrives at a Stay Greedless property, responsibilities may vary depending on
          the specific circumstances and location. Common host responsibilities include:
        </p>
        <ul>
          <li>
            Provide a warm welcome. If possible, greet the guest in person, introduce yourself, and
            help set a positive tone for the stay.
          </li>
          <li>
            Give a property tour if possible and explain how to use appliances, electronics, and
            amenities.
          </li>
          <li>
            Provide necessary information, such as emergency contact information, check-out
            instructions, Wi-Fi details, and house rules.
          </li>
          <li>
            Address any concerns or questions promptly and professionally so the guest can enjoy the
            stay.
          </li>
          <li>Be available for assistance during the guest&apos;s stay in case of emergencies.</li>
        </ul>
      </section>

      <section className={css.section}>
        <h2>22. Logo/Use of Our Name</h2>
        <p>
          Stay Greedless does not give anyone permission to use its logo for any purpose. You may
          not use the Stay Greedless name in a way that misleads someone into believing you are
          sponsored by Stay Greedless without Stay Greedless&apos;s permission.
        </p>
      </section>

      <section className={css.section}>
        <h2>23. Acceptable Use and Prohibited Activities</h2>
        <p>
          You may use Stay Greedless only for lawful marketplace activities. You may not use the
          platform, its payment services, communications tools, listings, accounts, or content to
          engage in, facilitate, promote, conceal, or assist any of the following:
        </p>
        <ul>
          <li>
            <strong>Illegal activity:</strong> Any activity that violates applicable law or
            regulation, including money laundering, terrorist financing, trafficking, sanctions
            evasion, the sale or distribution of illegal goods or services, or the use of a property
            for unlawful purposes.
          </li>
          <li>
            <strong>Fraud or deceptive practices:</strong> False, misleading, duplicate, or
            impersonated accounts or listings; misrepresentation of a property, identity,
            availability, price, fees, or affiliation; payment fraud; stolen payment credentials;
            chargeback abuse; collusion; review manipulation; phishing; scams; or attempts to evade
            platform fees, verification, restrictions, or enforcement.
          </li>
          <li>
            <strong>Harmful or abusive activity and content:</strong> Content or conduct that is
            threatening, harassing, hateful, discriminatory, defamatory, sexually exploitative,
            violent, unsafe, invasive of privacy, or reasonably likely to cause physical,
            emotional, or financial harm. Content involving the exploitation or endangerment of
            children is strictly prohibited.
          </li>
          <li>
            <strong>Intellectual property infringement:</strong> Posting, copying, distributing,
            displaying, or otherwise using content that infringes or misappropriates another
            person&apos;s copyright, trademark, patent, trade secret, publicity, privacy, or other
            proprietary rights, including removing ownership or copyright notices.
          </li>
          <li>
            <strong>Platform or security abuse:</strong> Introducing malware; scraping or accessing
            the platform through unauthorized automated means; probing or bypassing security
            controls; interfering with platform operation; reverse engineering; gaining
            unauthorized access to accounts, systems, or data; or collecting another user&apos;s
            information without authorization.
          </li>
          <li>
            <strong>Financial, legal, or reputational risk:</strong> Activity that exposes Stay
            Greedless, its users, payment partners, or service providers to excessive disputes,
            chargebacks, fraud, regulatory action, legal liability, financial loss, security risk,
            or material reputational harm, or that violates the rules of an applicable payment
            provider.
          </li>
        </ul>
        <p>
          Stay Greedless may investigate suspected violations and, where reasonably necessary,
          remove content, reject or cancel listings or reservations, restrict payment activity,
          suspend or terminate accounts, preserve relevant records, and cooperate with payment
          providers, affected parties, regulators, or law enforcement. Stay Greedless may take
          immediate action when necessary to protect users, the platform, or third parties, subject
          to applicable law.
        </p>

        <h3>23.1 Screening and Monitoring</h3>
        <p>
          Stay Greedless may use automated systems, manual review, identity and payment verification,
          user reports, third-party data, and service-provider risk controls to screen accounts,
          listings, content, communications, reservations, and payment activity before or after they
          appear on the platform. We may request additional information or documentation and may
          delay or prevent publication, booking, payment, or settlement while a review is pending.
          These controls do not create an obligation to monitor every user, listing, communication,
          or transaction, and users remain responsible for their conduct and content.
        </p>

        <h3>23.2 Enforcement Measures</h3>
        <p>
          Depending on the nature, severity, frequency, and risk of a suspected or confirmed
          violation, Stay Greedless may issue an educational notice or warning; require corrective
          action or additional verification; remove or restrict content or listings; limit account
          features, communications, reservations, or payment activity; cancel a reservation; suspend
          or terminate an account; prevent a person or property from returning to the platform; or
          refer the matter to a payment provider, rights holder, affected party, regulator, or law
          enforcement agency. We may act without prior notice when reasonably necessary to prevent
          harm, fraud, unlawful activity, evidence destruction, financial loss, or a violation of a
          legal or payment-provider requirement.
        </p>

        <h3>23.3 Reporting and Escalation</h3>
        <p>
          Users and third parties may report suspected prohibited activity through the Contact Us
          page or by emailing{' '}
          <a href="mailto:hello@staygreedless.com">hello@staygreedless.com</a>. Reports should
          identify the relevant user, listing, reservation, communication, or transaction and
          include available supporting information. Intellectual property owners or their
          authorized representatives should also identify the protected work or right, the
          allegedly infringing material and its location, their contact information, and a
          statement explaining the basis of the claim.
        </p>
        <p>
          Law enforcement agencies and regulators may submit preservation requests, subpoenas,
          court orders, regulatory inquiries, or other official correspondence to{' '}
          <a href="mailto:hello@staygreedless.com">hello@staygreedless.com</a> using an official
          government email address and clearly identifying the issuing authority, responsible
          official, legal basis, requested records, applicable deadline, and contact information.
          Stay Greedless may preserve and disclose information when required or permitted by
          applicable law. Emergencies involving an imminent threat to life or physical safety
          should first be reported to the appropriate local emergency service.
        </p>
      </section>

      <section className={css.section}>
        <h2>24. Acceptance</h2>
        <ul>
          <li>Accessing this website means agreeing to these terms.</li>
          <li>All users must follow applicable laws.</li>
          <li>Content is protected by copyright and trademark laws.</li>
        </ul>
      </section>

      <section className={css.section}>
        <h2>25. Use of License</h2>
        <ul>
          <li>License type: limited, non-exclusive, and revocable.</li>
          <li>Usage: personal, non-commercial view only.</li>
        </ul>
      </section>

      <section className={css.section}>
        <h2>26. Disclaimer</h2>
        <ul>
          <li>Stay Greedless does not own or manage any property listed on this platform.</li>
          <li>The listing host sets prices, fees, and check-in/check-out times.</li>
        </ul>
      </section>

      <section className={css.section}>
        <h2>27. Liability</h2>
        <p>Stay Greedless holds no liability for damages arising from use of the website.</p>
      </section>

      <section className={css.section}>
        <h2>28. Revisions</h2>
        <ul>
          <li>Materials may contain errors.</li>
          <li>Content can change without notice.</li>
        </ul>
      </section>

      <section className={css.section}>
        <h2>29. Links</h2>
        <p>Stay Greedless is not responsible for external links, and no endorsement is implied.</p>
      </section>

      <section className={css.section}>
        <h2>30. Modifications</h2>
        <p>These terms can change without notice.</p>
      </section>

      <section className={css.section}>
        <h2>31. Privacy</h2>
        <p>See our Privacy Policy for details about how we handle data.</p>
      </section>

      <section className={css.section}>
        <h2>32. Legal Entity and Notices</h2>
        <p>
          The Stay Greedless platform is operated by Stay Greedless, a business organized and
          operating in the District of Columbia, with its principal business address at 1629 K
          Street NW, Suite 300, Washington, DC 20006.
        </p>
        <p>
          Stay Greedless has not identified a separate registered agent for purposes of these Terms.
          Until registered-agent information is formally designated and published, legal notices
          intended for Stay Greedless must be delivered to the principal business address above,
          with a copy sent to{' '}
          <a href="mailto:hello@staygreedless.com">hello@staygreedless.com</a>. This notice method
          does not limit any method of service required or permitted by applicable law.
        </p>
      </section>

      <section className={css.section}>
        <h2>33. Governing Law and Venue</h2>
        <p>
          These Terms and any dispute arising from or relating to the platform or these Terms are
          governed by the laws of the District of Columbia, without regard to its conflict-of-laws
          principles. Subject to any mandatory consumer-protection law that applies, the state and
          federal courts located in the District of Columbia will have exclusive jurisdiction and
          venue over such disputes, and each party consents to the personal jurisdiction of those
          courts.
        </p>
      </section>
    </div>
  );
};

export default TermsOfService;
